import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

import { IndicacaoService } from '../../services/indicacoes/indicacao.service';
import { isEmailValido } from '../../shared/utils/email.util';
import {
  ComissaoConsultaDTO,
  IndicacaoConsultaDTO,
  OrigemIndicacao,
  RankingIndicacoesConsultaDTO,
  ResumoCupomIndicacaoConsultaDTO,
  SelecaoCupomIndicacaoConsultaDTO,
  StatusIndicacao,
  StatusRepasse,
  StatusRepasseResumo,
} from '../../models/indicacoes/indicacao.model';

/** Severities aceitas pelo p-tag do PrimeNG. */
type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';

/**
 * Pagina do Programa de Indicacoes.
 *
 * Visao do proprio usuario (a listagem master de cupons virou tela propria em
 * `/master/cupons`). Concentra:
 * - card do link/codigo de indicacao + botao copiar;
 * - card de resumo de totais;
 * - tabela paginada (lazy) das indicacoes;
 * - dialog de convite por email;
 * - dialog de detalhe (drill-down) das competencias, com repasse master-only.
 *
 * Fonte da verdade: `vendas/docs/api/API_CONTRACT_indicacoes.md`.
 */
@Component({
  selector: 'app-indicacoes',
  templateUrl: './indicacoes.component.html',
  styleUrls: ['./indicacoes.component.scss'],
  providers: [MessageService],
})
export class IndicacoesComponent implements OnInit, OnDestroy {
  // ===========================================================================
  // Seletor de cupom + link/codigo por escopo (visao do usuario)
  // ===========================================================================

  /** Itens brutos do seletor (cupons ativos), vindos de E3. */
  cuponsSelecao: SelecaoCupomIndicacaoConsultaDTO[] = [];
  /** Opcoes do p-dropdown: "Geral" (value=null) + cada cupom (value=cupomId). */
  escopoOptions: { label: string; value: number | null }[] = [];
  /** Selecao atual do dropdown. null = GERAL (default). */
  cupomSelecionadoId: number | null = null;
  /** Resumo do escopo selecionado — dirige o card "Meu link" e os tiles. */
  resumoEscopo: ResumoCupomIndicacaoConsultaDTO | null = null;
  /** Load inicial da aba (E3). */
  cuponsLoading = true;
  /** Troca de escopo em andamento (E4). */
  resumoLoading = false;
  /** Erro no load inicial (E3). */
  resumoErro = false;

  /**
   * Ranking da empresa nos tiles do resumo (GET /indicacoes/meu-ranking).
   * Global da empresa (nao varia com o escopo/cupom). Best-effort: em erro fica
   * null e os badges simplesmente nao aparecem (tiles seguem funcionando).
   */
  ranking: RankingIndicacoesConsultaDTO | null = null;

  // Card "Meu link": campos derivados de `resumoEscopo` (sem estado duplicado).
  /** True quando o escopo atual e GERAL (oculta codigo/link/percentuais). */
  get escopoGeral(): boolean {
    return (this.resumoEscopo?.escopo ?? 'GERAL') === 'GERAL';
  }
  get codigoIndicacao(): string {
    return this.resumoEscopo?.codigoIndicacao ?? '';
  }
  get linkDeIndicacao(): string {
    return this.resumoEscopo?.linkDeIndicacao ?? '';
  }
  get porcentagemDesconto(): number | null {
    return this.resumoEscopo?.porcentagemDesconto ?? null;
  }
  get porcentagemComissao(): number | null {
    return this.resumoEscopo?.porcentagemComissao ?? null;
  }
  get tipoPeriodo(): string | null {
    return this.resumoEscopo?.tipoPeriodo ?? null;
  }
  /** Validade do cupom em escopo (visao do usuario). null no GERAL. */
  get dataValidade(): string | null {
    return this.resumoEscopo?.dataValidade ?? null;
  }
  /**
   * True quando o cupom em escopo esta vencido: o usuario ve as metricas, mas o
   * compartilhamento (link/copiar/WhatsApp/convidar) fica bloqueado.
   */
  get escopoVencido(): boolean {
    return !this.escopoGeral && this.cupomVencido(this.dataValidade);
  }

  // ===========================================================================
  // Listagem (tabela lazy)
  // ===========================================================================

  indicacoes: IndicacaoConsultaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  loading = true;
  erroCarregar = false;

  /** Opcoes do dropdown "itens por pagina" do footer. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Ultimo valor de "itens por pagina" ja carregado; usado para descartar o
   *  (ngModelChange) que o dropdown dispara sem o usuario mudar a selecao. */
  private _lastRows = 10;

  /** Campo de ordenacao por cabecalho (campo do backend); null = sem ordenacao
   *  de header (mantem o default 'id,desc'). Setado ao clicar num `th`
   *  ordenavel (apenas "E-mail do amigo"/emailIndicado e "Situacao"/status). */
  private sortField: string | null = null;

  /** Direcao da ordenacao por cabecalho: 1 = asc, -1 = desc, 0 = sem ordenacao. */
  private sortOrder = 0;

  /** Ultima chave (page|size|sort) disparada pelo onLazyLoad, usada como
   *  loop-guard: a reatribuicao de [value]/[first] apos a carga pode reemitir o
   *  onLazyLoad com os MESMOS parametros — ignoramos a 2a emissao identica.
   *  As acoes (convite, repasse) e o paginador chamam carregarIndicacoes
   *  diretamente, sem passar por aqui, entao nunca sao engolidas por este guard. */
  private _ultimaLazyKey: string | null = null;

  // ===========================================================================
  // Dialog de convite
  // ===========================================================================

  conviteDialogVisivel = false;
  conviteEmail = '';
  enviandoConvite = false;

  /** Dialog informativo "Como funciona o desconto e a comissao". */
  infoDialogVisivel = false;

  // ===========================================================================
  // Dialog de detalhe (drill-down)
  // ===========================================================================

  detalheDialogVisivel = false;
  indicacaoSelecionada: IndicacaoConsultaDTO | null = null;
  comissoes: ComissaoConsultaDTO[] = [];
  carregandoComissoes = false;
  /** id da comissao em processamento de repasse (para loading por linha). */
  repassandoComissaoId: number | null = null;

  // ===========================================================================
  // Permissao master (acao de repasse)
  // ===========================================================================

  acessoMaster = false;

  private langChangeSub?: Subscription;

  constructor(
    private indicacaoService: IndicacaoService,
    private messageService: MessageService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  // ===========================================================================
  // Ciclo de vida
  // ===========================================================================

  ngOnInit(): void {
    this.carregarAcessoMaster();
    this.carregarMeusCupons();
    this.carregarRanking();
    // A primeira carga da tabela vem do (onLazyLoad), que a p-table dispara na
    // inicializacao — chamar carregarIndicacoes aqui geraria um request duplicado.

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      // Reconstroi as opcoes do seletor (o rotulo "Geral" usa string estatica).
      this.buildEscopoOptions();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  /** Le `categoriaAcessoMaster` da sessao de forma SSR-safe (padrao `MenuLateralComponent`). */
  private carregarAcessoMaster(): void {
    if (isPlatformBrowser(this.platformId)) {
      const valor =
        sessionStorage.getItem('categoriaAcessoMaster') ||
        localStorage.getItem('categoriaAcessoMaster');
      this.acessoMaster = valor === 'true';
    }
  }

  // ===========================================================================
  // Card de link / codigo — seletor de cupom + resumo por escopo
  // ===========================================================================

  /**
   * Load inicial da aba "Meus referidos" (E3): busca os cupons ativos da
   * empresa (seletor) e o resumo GERAL inicial. Inicia sempre em GERAL (CA01).
   */
  carregarMeusCupons(): void {
    this.cuponsLoading = true;
    this.resumoErro = false;
    this.indicacaoService.getMeusCupons().subscribe({
      next: (res) => {
        this.cuponsSelecao = res.cupons || [];
        this.buildEscopoOptions();
        this.cupomSelecionadoId = null; // GERAL default
        this.resumoEscopo = res.resumoGeral;
        this.cuponsLoading = false;
      },
      error: () => {
        this.resumoErro = true;
        this.cuponsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('indicacoes.seletor.erro_carregar'),
        });
      },
    });
  }

  /**
   * Carrega o ranking dos tiles (best-effort). Global da empresa: uma unica
   * chamada no init, sem recarregar na troca de escopo. Em erro, apenas
   * mantem `ranking = null` (sem toast — o badge e informativo).
   */
  carregarRanking(): void {
    this.indicacaoService.getMeuRanking().subscribe({
      next: (res) => (this.ranking = res),
      error: () => (this.ranking = null),
    });
  }

  /**
   * Reconstroi as opcoes do seletor: "Geral" (value=null) + cada cupom ativo. Os
   * cupons vencidos sao listados tambem (como o master) com o sufixo "(vencido)";
   * o compartilhamento deles fica bloqueado no card "Meu link".
   */
  private buildEscopoOptions(): void {
    this.escopoOptions = [
      { label: this.translate.instant('indicacoes.seletor.geral'), value: null },
      ...this.cuponsSelecao.map((c) => ({
        label: this.cupomVencido(c.dataValidade)
          ? `${c.codigoIndicacao} (${this.translate.instant('indicacoes.cupom_detalhe.vencido')})`
          : c.codigoIndicacao,
        value: c.cupomId as number | null,
      })),
    ];
  }

  /**
   * Troca de escopo no dropdown (E4): recarrega o resumo (tiles) e o card link
   * conforme a selecao. null => GERAL (sem query); id => CUPOM. No erro, mantem
   * o ultimo escopo valido visivel (nao "pisca" a tela) e comunica via toast.
   */
  onEscopoChange(): void {
    const cupomId = this.cupomSelecionadoId ?? undefined;

    // 1) Tiles de resumo (fluxo existente, mantido).
    this.resumoLoading = true;
    this.indicacaoService.getMeuResumo(cupomId).subscribe({
      next: (res) => {
        this.resumoEscopo = res;
        this.resumoLoading = false;
      },
      error: (err) => {
        this.resumoLoading = false;
        this.tratarErroResumo(err);
      },
    });

    // 2) Tabela "Minhas indicacoes" em sincronia com os tiles (FE-1 / CA01).
    // Volta a primeira pagina e invalida o loop-guard antes da recarga direta.
    this.first = 0;
    this._ultimaLazyKey = null;
    this.carregarIndicacoes(0, this.rows);
  }

  /** Erro na troca de escopo (E4). 404 anti-IDOR => "cupom nao encontrado". */
  private tratarErroResumo(err: any): void {
    if (err?.status === 404) {
      this.toastErro(this.translate.instant('indicacoes.seletor.erro_cupom_nao_encontrado'));
      return;
    }
    this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
  }

  /**
   * Abre o WhatsApp com uma mensagem pronta contendo o link de indicacao, para
   * o usuario compartilhar com um contato. Usa wa.me (funciona em web e mobile).
   */
  compartilharWhatsApp(): void {
    if (!this.linkDeIndicacao || this.escopoVencido || !isPlatformBrowser(this.platformId)) {
      return;
    }
    // Marca a origem como WHATSAPP anexando &canal=whatsapp APENAS ao link que
    // vai dentro da mensagem (o link do campo "copiar" fica intacto). O separador
    // segue o que o link ja tem (?ref= => usa &); o encodeURIComponent do texto
    // preserva o `&` como parte da mensagem (nao quebra a URL do wa.me).
    const separador = this.linkDeIndicacao.includes('?') ? '&' : '?';
    const linkComCanal = `${this.linkDeIndicacao}${separador}canal=whatsapp`;
    const mensagem = this.translate.instant('indicacoes.whatsapp.mensagem', {
      link: linkComCanal,
    });
    const url = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }

  /**
   * Rotulo localizado da origem detalhada da indicacao. Valores desconhecidos ou
   * ausentes retornam "-" (robustez contra respostas antigas sem o campo).
   */
  labelOrigem(origem: OrigemIndicacao | string | null | undefined): string {
    const chaves: Record<string, string> = {
      LINK: 'link',
      EMAIL: 'email',
      WHATSAPP: 'whatsapp',
      CUPOM: 'cupom',
      CONTADOR: 'contador',
    };
    const sufixo = origem ? chaves[String(origem).toUpperCase()] : undefined;
    if (!sufixo) {
      return '-';
    }
    return this.translate.instant(`indicacoes.origem.${sufixo}`);
  }

  /** Icone (PrimeIcons) correspondente a origem detalhada da indicacao. */
  iconeOrigem(origem: OrigemIndicacao | string | null | undefined): string {
    switch (origem ? String(origem).toUpperCase() : '') {
      case 'EMAIL':
        return 'pi pi-envelope';
      case 'LINK':
        return 'pi pi-link';
      case 'WHATSAPP':
        return 'pi pi-whatsapp';
      case 'CUPOM':
        return 'pi pi-ticket';
      case 'CONTADOR':
        return 'pi pi-briefcase';
      default:
        return '';
    }
  }

  /** Rotulo localizado da cadencia de repasse do cupom (MENSAL / A_VISTA). */
  tipoPeriodoLabel(valor: string | null): string {
    if (!valor) {
      return '';
    }
    const chave = `indicacoes.cupom_detalhe.periodo.${valor.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? valor : traduzido;
  }

  /** Copia o link de indicacao para a area de transferencia (SSR-safe). */
  copiarLink(): void {
    if (!this.linkDeIndicacao || this.escopoVencido || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const sucesso = () =>
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('indicacoes.link_copiado'),
      });

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.linkDeIndicacao).then(sucesso, () => this.copiarLinkFallback(sucesso));
    } else {
      this.copiarLinkFallback(sucesso);
    }
  }

  /** Fallback de copia quando a Clipboard API nao esta disponivel. */
  private copiarLinkFallback(sucesso: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = this.linkDeIndicacao;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      sucesso();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('indicacoes.erros.generico'),
      });
    }
  }

  // ===========================================================================
  // Listagem + resumo
  // ===========================================================================

  /**
   * Monta o parametro `sort` (formato Spring "campo,asc|desc"). Com ordenacao
   * de cabecalho ativa usa o campo/direcao escolhidos; sem ela, MANTEM o default
   * historico 'id,desc'. Restrito aos campos suportados pelo backend
   * (emailIndicado, status); comissao/repasse sao calculados e retornam 500.
   */
  private montarSort(): string {
    if (!this.sortField || this.sortOrder === 0) {
      return 'id,desc';
    }
    return `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  carregarIndicacoes(page: number = 0, size: number = this.rows): void {
    this.loading = true;
    this.erroCarregar = false;
    const sort = this.montarSort();
    // Escopo por cupom (consulta_escopo_cupom / FE-1): a tabela segue o seletor.
    // Sem cupom selecionado, `undefined` e descartado por buildQueryParams e a
    // query fica identica a atual (regressao zero — CA05).
    const cupomId = this.cupomSelecionadoId ?? undefined;

    this.indicacaoService.listar(page, size, sort, cupomId).subscribe({
      next: (res) => {
        // Os tiles de resumo sao dirigidos por `resumoEscopo` (E3/E4), nao por
        // esta listagem — aqui so mantemos a lista/paginacao da p-table.
        this.indicacoes = res.indicacoes?.content || [];
        this.totalRecords = res.indicacoes?.totalElements ?? 0;
        this.first = page * size;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        // 404 anti-BOLA (cupom stale ou de outra empresa): mantem a lista atual
        // ("nao pisca") e apenas comunica por toast (OBS-1).
        if (err?.status === 404) {
          this.toastErro(
            this.translate.instant('indicacoes.seletor.erro_cupom_nao_encontrado'),
          );
          return;
        }
        this.indicacoes = [];
        this.totalRecords = 0;
        this.erroCarregar = true;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('indicacoes.tabela.erro_carregar'),
        });
      },
    });
  }

  /** Carregamento lazy da tabela (paginacao + ordenacao server-side). */
  onLazyLoad(event: TableLazyLoadEvent): void {
    // Tabela [lazy]: clicar num cabecalho `pSortableColumn` dispara o PROPRIO
    // onLazyLoad ja com sortField/sortOrder — capturamos o sort aqui (sem um
    // (onSort) separado, que causaria recarga dupla). Ordenacao simples =>
    // sortField string; sem ordenacao => null/undefined.
    const novoSortField = typeof event.sortField === 'string' ? event.sortField : null;
    const novoSortOrder = novoSortField ? (event.sortOrder ?? 0) : 0;
    const sortMudou = novoSortField !== this.sortField || novoSortOrder !== this.sortOrder;

    const size = event.rows ?? this.rows;
    this.rows = size;

    // Ao trocar a ordenacao, volta para a primeira pagina.
    if (sortMudou) {
      this.sortField = novoSortField;
      this.sortOrder = novoSortOrder;
      this.first = 0;
    }
    const page = sortMudou ? 0 : Math.floor((event.first ?? 0) / size);

    // Loop-guard: ignora a reemissao IDENTICA do onLazyLoad provocada pela
    // reatribuicao de [value]/[first] apos a carga. Uma troca real de
    // ordenacao/pagina muda a chave e passa normalmente.
    // A chave representa a identidade completa da consulta; `cupomId` faz parte
    // dela (D2) — sem ele, "pagina 0 do cupom X" e "do cupom Y" colidiriam.
    const chave = `${page}|${size}|${this.montarSort()}|${this.cupomSelecionadoId ?? ''}`;
    if (chave === this._ultimaLazyKey) {
      return;
    }
    this._ultimaLazyKey = chave;

    this.carregarIndicacoes(page, size);
  }

  /** Troca de pagina pelo paginador externo. Como a tabela e lazy,
   *  recarregamos a pagina solicitada do backend. */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRows = size;
    this.carregarIndicacoes(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega.
   *  O retorno antecipado ignora o disparo do dropdown sem mudanca de valor. */
  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.carregarIndicacoes(0, this.rows);
  }

  // ===========================================================================
  // Dialog de convite
  // ===========================================================================

  abrirDialogConvite(): void {
    // Com um cupom vencido em escopo, o convite fica bloqueado: embora o backend
    // (Regra C) escolha outro cupom valido, seria confuso convidar "por" um cupom
    // que a tela mostra como vencido. O usuario deve trocar o escopo primeiro.
    if (this.escopoVencido) {
      return;
    }
    this.conviteEmail = '';
    this.conviteDialogVisivel = true;
  }

  fecharDialogConvite(): void {
    this.conviteDialogVisivel = false;
  }

  submitConvite(): void {
    if (this.enviandoConvite) {
      return;
    }

    const email = (this.conviteEmail || '').trim();

    if (!email) {
      this.toastErro(this.translate.instant('indicacoes.erros.email_obrigatorio'));
      return;
    }
    if (!isEmailValido(email)) {
      this.toastErro(this.translate.instant('indicacoes.erros.email_invalido'));
      return;
    }

    this.enviandoConvite = true;
    this.indicacaoService.enviarConvite(email).subscribe({
      next: () => {
        this.enviandoConvite = false;
        this.conviteDialogVisivel = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('indicacoes.convite.sucesso'),
        });
        this.carregarIndicacoes(0, this.rows);
      },
      error: (err) => {
        this.enviandoConvite = false;
        this.tratarErroConvite(err);
      },
    });
  }

  private tratarErroConvite(err: any): void {
    const status = err?.status;
    if (status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: err?.error?.message || this.translate.instant('indicacoes.erros.email_ja_cliente'),
      });
      return;
    }
    if (status === 400) {
      const detalhe =
        err?.error?.errors?.[0]?.mensagem ||
        err?.error?.message ||
        this.translate.instant('indicacoes.erros.email_invalido');
      this.toastErro(detalhe);
      return;
    }
    this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
  }

  // ===========================================================================
  // Dialog de detalhe (drill-down)
  // ===========================================================================

  abrirDetalhe(indicacao: IndicacaoConsultaDTO): void {
    this.carregarDetalhe(indicacao, this.indicacaoService.listarComissoes(indicacao.id));
  }

  /** Carrega as competencias no dialog de detalhe. */
  private carregarDetalhe(
    indicacao: IndicacaoConsultaDTO,
    fonte: Observable<ComissaoConsultaDTO[]>,
  ): void {
    this.indicacaoSelecionada = indicacao;
    this.comissoes = [];
    this.detalheDialogVisivel = true;
    this.carregandoComissoes = true;

    fonte.subscribe({
      next: (res) => {
        this.comissoes = res || [];
        this.carregandoComissoes = false;
      },
      error: (err) => {
        this.carregandoComissoes = false;
        if (err?.status === 404) {
          this.detalheDialogVisivel = false;
          this.toastErro(
            err?.error?.message || this.translate.instant('indicacoes.erros.indicacao_nao_encontrada'),
          );
          return;
        }
        this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
      },
    });
  }

  fecharDetalhe(): void {
    this.detalheDialogVisivel = false;
    this.indicacaoSelecionada = null;
    this.comissoes = [];
  }

  /** Indica se a acao "Marcar como pago" deve aparecer para a competencia. */
  podeMarcarPago(comissao: ComissaoConsultaDTO): boolean {
    return this.acessoMaster && comissao.statusRepasse === 'PENDENTE';
  }

  marcarComoPago(comissao: ComissaoConsultaDTO): void {
    if (this.repassandoComissaoId !== null) {
      return;
    }
    this.repassandoComissaoId = comissao.id;

    this.indicacaoService.marcarRepassePago(comissao.id).subscribe({
      next: (atualizada) => {
        this.repassandoComissaoId = null;
        // Atualiza a linha no detalhe.
        this.comissoes = this.comissoes.map((c) => (c.id === atualizada.id ? atualizada : c));
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('indicacoes.repasse.sucesso'),
        });
        // Totais e listagem mudam — recarrega resumo + tabela.
        this.carregarIndicacoes(this.first / this.rows, this.rows);
      },
      error: (err) => {
        this.repassandoComissaoId = null;
        this.tratarErroRepasse(err);
      },
    });
  }

  private tratarErroRepasse(err: any): void {
    const status = err?.status;
    if (status === 403) {
      this.toastErro(this.translate.instant('indicacoes.erros.repasse_sem_permissao'));
      return;
    }
    if (status === 404) {
      this.toastErro(this.translate.instant('indicacoes.erros.comissao_nao_encontrada'));
      return;
    }
    if (status === 422) {
      this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
      return;
    }
    this.toastErro(err?.error?.message || this.translate.instant('indicacoes.erros.generico'));
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /** Traduz o status da indicacao (StatusIndicacao). */
  labelStatusIndicacao(status: StatusIndicacao | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Traduz o status de repasse resumido da indicacao (pode ser null). */
  labelStatusRepasseResumo(status: StatusRepasseResumo): string {
    if (!status) {
      return '-';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Traduz o status de repasse de uma comissao (drill-down). */
  labelStatusRepasse(status: StatusRepasse | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`indicacoes.status.${String(status).toLowerCase()}`);
  }

  /** Severity do p-tag conforme o status (cor por significado). */
  severityStatusIndicacao(status: StatusIndicacao | string | undefined): TagSeverity {
    switch (status) {
      case 'CONFIRMADA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'CANCELADA':
        return 'secondary';
      default:
        return 'info';
    }
  }

  severityStatusRepasse(status: string | null | undefined): TagSeverity {
    switch (status) {
      case 'PAGA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'PARCIAL':
        return 'info';
      case 'CANCELADA':
        return 'secondary';
      default:
        return 'info';
    }
  }

  /** Linha de comissao CANCELADA — exibida esmaecida (historico). */
  isComissaoCancelada(comissao: ComissaoConsultaDTO): boolean {
    return comissao.statusRepasse === 'CANCELADA';
  }

  /** Converte competencia "AAAA-MM" em "MM/AAAA" (sem timezone). */
  formatarCompetencia(competencia: string | null | undefined): string {
    if (!competencia) {
      return '';
    }
    const partes = competencia.split('-');
    if (partes.length < 2) {
      return competencia;
    }
    return `${partes[1]}/${partes[0]}`;
  }

  /** True quando o cupom nao tem validade (sentinela ano >= 2100 ou data ausente/invalida). */
  cupomSemValidade(dataISO: string | null | undefined): boolean {
    if (!dataISO) {
      return true;
    }
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return true;
    }
    return data.getFullYear() >= 2100;
  }

  /** True quando o cupom esta vencido: data real (ano < 2100) anterior a agora. */
  cupomVencido(dataISO: string | null | undefined): boolean {
    if (!dataISO || this.cupomSemValidade(dataISO)) {
      return false; // sem validade / sentinela nunca vence
    }
    const data = new Date(dataISO);
    return data.getTime() < Date.now();
  }

  /** Formata data ISO 8601 em dd/MM/yyyy (padrao das telas do projeto). */
  formatarData(dataISO: string | null | undefined): string {
    if (!dataISO) {
      return '';
    }
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  // ===========================================================================
  // Util
  // ===========================================================================

  private toastErro(detalhe: string): void {
    this.messageService.add({
      severity: 'error',
      summary: detalhe,
    });
  }
}
